import { Container } from '@tryhackme/thm-ui-components';
import styled from 'styled-components';

export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 8rem auto;
  padding-bottom: 8rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin: 6rem auto;
  }
`;

export const StyledHeading = styled.h2`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  line-height: 1.149;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  margin: 0 0 4.6rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 2.8rem;
  }
`;

export const StyledStatsContainer = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  gap: 2.4rem;
  width: 100%;
  margin: 0 0 2.4rem;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    flex-direction: column;
  }
`;

export const StyledStatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.4rem;
  padding: 3.2rem;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 0.5rem;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 2.4rem;
    gap: 2rem;
  }
`;

export const StyledMetric = styled.div`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.title};
  line-height: 1.149;
  text-align: center;
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  
  ${({ theme }) =>
    theme.colors.secondary.main
      ? `
    background: linear-gradient(90deg, rgba(82, 123, 211, 1) 0%, rgba(158, 179, 225, 1) 58%, rgba(167, 231, 255, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `
      : `
    color: #A3EA2A;
  `}

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  }
`;

export const StyledStatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

export const StyledStatTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2rem;
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.medium};
  }
`;

export const StyledStatDescription = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;
  max-width: 36rem;
  align-self: center;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.small};
  }
`;

export const StyledTestimonialsWrapper = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  margin-bottom: 4.6rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-bottom: 3.2rem;
  }
`;

export const StyledCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

export const StyledCarouselTrack = styled.div<{ $currentSlide: number }>`
  display: flex;
  gap: 2.4rem;
  transition: transform 0.5s ease-in-out;
  transform: translateX(${({ $currentSlide }) => `calc(-${$currentSlide} * (50% + 1.2rem))`});

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    transform: translateX(${({ $currentSlide }) => `calc(-${$currentSlide} * (100% + 2.4rem))`});
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 1.6rem;
    transform: translateX(${({ $currentSlide }) => `calc(-${$currentSlide} * (100% + 1.6rem))`});
  }
`;

export const StyledTestimonialCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  padding: 3.2rem;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 0.5rem;
  width: calc(50% - 1.2rem);
  min-height: 41.7rem;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    width: 100%;
    min-height: auto;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 2.4rem;
    gap: 1.4rem;
  }
`;

export const StyledTestimonialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.8rem;
`;

export const StyledCompanyLogo = styled.img<{ $logoWidth?: number }>`
  max-width: ${({ $logoWidth }) => ($logoWidth ? `${$logoWidth}rem` : '14.3rem')};
  max-height: 5.2rem;
  object-fit: contain;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    max-width: 11.8rem;
    max-height: 4.2rem;
  }
`;

export const StyledIndustryTag = styled.span`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  color: rgba(113, 156, 249, 1);
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.small};
  }
`;

export const StyledTestimonialContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 3.2rem;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 2.4rem;
  }
`;

export const StyledTestimonialQuote = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2rem;
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.grey[400]};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
  }
`;

export const StyledTestimonialAuthor = styled.p`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: ${({ theme }) => theme.fonts.sizes.default};
  }
`;

export const StyledDotsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    gap: 0.5rem;
  }
`;

export const StyledDot = styled.button<{ $isActive?: boolean }>`
  width: ${({ $isActive }) => ($isActive ? '5.2rem' : '1.3rem')};
  height: 0.5rem;
  border: none;
  border-radius: 0.5rem;
  background: ${({ $isActive, theme }) => ($isActive ? theme.colors.info.main : '#3E475A')};
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;
